/**
 * The notices this person has had, grouped by the hire or leaver the work is
 * for, with what needs them above what merely happened (PRD G1: no task moves
 * without an explicit notification). The same notices are mailed, as often and
 * as much as the person chooses here (the mails link to this screen); this
 * list is complete whether or not a mail went.
 *
 * Opening the list marks what is on it read. What was unread when it opened
 * stays drawn as unread until the person leaves, so they can still see what
 * was new.
 */

import { useEffect, useMemo, useState } from "react";
import {
  MAIL_EVERY,
  MAIL_EVERY_OPTIONS,
  MAIL_WHAT,
  MAIL_WHAT_OPTIONS,
  type MailEvery as Every,
  type MailWhat as What,
  type Notice,
} from "@utuwrk/core";
import { api as account, sentence } from "../lib/api.js";
import { groupNotices } from "../lib/notices.js";
import { useOrg } from "../lib/org.js";
import { whenText } from "../lib/words.js";
import { EmptyState, Link, Loading, PageHeader, Problem, useLoad } from "./bits.js";

export function Notices() {
  const { api, refreshUnread } = useOrg();
  const { data, error } = useLoad(() => api.notices(), [api]);
  const groups = useMemo(() => groupNotices(data?.notices ?? []), [data]);
  const waiting = groups.reduce((sum, group) => sum + group.fresh, 0);

  useEffect(() => {
    const newest = data?.notices[0];
    if (!newest || data.unread === 0) return;
    void api.readNotices(newest.id).then(refreshUnread, () => undefined);
  }, [data, api, refreshUnread]);

  if (!data && !error) return <Loading title="Notices" />;

  return (
    <section className="page">
      <PageHeader
        title="Notices"
        eyebrow={
          data && data.notices.length > 0
            ? waiting > 0
              ? `${waiting} new that need you`
              : "Nothing new needs you"
            : undefined
        }
      />
      <Problem>{error}</Problem>
      {data && data.notices.length === 0 ? (
        <EmptyState icon="bell" title="No notices yet">
          You get one when a card becomes yours, or something you hold needs attention.
        </EmptyState>
      ) : null}
      {groups.map((group) => (
        <div className="panel" key={group.name}>
          <section className="zone">
            <h2 className="zone-title">
              {group.name} <span className="zone-count">{group.needsYou.length + group.happened.length}</span>
            </h2>
            {group.needsYou.length > 0 ? (
              <ul className="cards">
                {group.needsYou.map((notice) => (
                  <li key={notice.id}>
                    <NoticeCard notice={notice} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="empty-line">Nothing here needs you.</p>
            )}
            {group.happened.length > 0 ? (
              <details className="zone-done">
                <summary>What happened ({group.happened.length})</summary>
                <ul className="cards">
                  {group.happened.map((notice) => (
                    <li key={notice.id}>
                      <NoticeCard notice={notice} />
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </section>
        </div>
      ))}
      {data ? (
        <details className="panel zone-done">
          <summary>Email settings</summary>
          <MailSettings every={data.mailEvery} what={data.mailWhat} />
        </details>
      ) : null}
    </section>
  );
}

function NoticeCard({ notice }: { notice: Notice }) {
  return (
    <Link
      to={
        notice.why === "week-ahead"
          ? { name: "week" }
          : notice.employeeId
            ? { name: "employee", id: notice.employeeId }
            : { name: "card", id: notice.instanceId, key: notice.taskKey }
      }
      className={`card card-link${notice.read ? " card-later" : ""}`}
    >
      <div className="card-top">
        <p className={`card-title${notice.read ? "" : " unread"}`}>{notice.text}</p>
        {notice.urgent ? <span className="pill s-danger">Urgent</span> : null}
      </div>
      <p className="card-meta">
        {notice.resolved ? <span className="chip">Done</span> : null}
        {notice.read ? null : <span className="chip chip-accent">New</span>}
        <span>{whenText(notice.at)}</span>
      </p>
    </Link>
  );
}

/**
 * Which of these are mailed, and how often. Each saves as soon as it is
 * picked; they are the account's, so the same in every organisation.
 */
function MailSettings({ every: initialEvery, what: initialWhat }: { every: Every; what: What }) {
  const [every, setEvery] = useState(initialEvery);
  const [what, setWhat] = useState(initialWhat);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function save(apply: () => Promise<void>, undo: () => void) {
    setBusy(true);
    setProblem(null);
    try {
      await apply();
    } catch (failure) {
      undo();
      setProblem(sentence(failure));
    } finally {
      setBusy(false);
    }
  }

  const chooseWhat = (next: What) => {
    const was = what;
    setWhat(next);
    return save(
      async () => setWhat((await account.setMailWhat(next)).mailWhat),
      () => setWhat(was),
    );
  };
  const chooseEvery = (next: Every) => {
    const was = every;
    setEvery(next);
    return save(
      async () => setEvery((await account.setMailEvery(next)).mailEvery),
      () => setEvery(was),
    );
  };

  return (
    <>
      <label className="field">
        Email me
        <select
          value={what}
          disabled={busy}
          onChange={(event) => void chooseWhat(event.target.value as What)}
        >
          {MAIL_WHAT_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {MAIL_WHAT[option].label}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        How often
        <select
          value={every}
          disabled={busy}
          onChange={(event) => void chooseEvery(event.target.value as Every)}
        >
          {MAIL_EVERY_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {MAIL_EVERY[option].label}
            </option>
          ))}
        </select>
      </label>
      <p className="muted">
        Everything is always here in the app. Email goes for what needs you, or for everything if you choose;
        urgent ones always go. The first notice after a quiet spell is mailed at once, and what follows inside
        the window comes together in one mail. "Only urgent ones" mails just those, straight away; "Never
        email me" mails nothing.
      </p>
      <Problem>{problem}</Problem>
    </>
  );
}
